import { CircleCheck, CircleX, History, Hourglass, Info, RefreshCw, ScanSearch, ShieldCheck, TrendingDown, TriangleAlert } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Badge, Button, cn, EmptyState, ErrorState, formatDuration, formatInt, Gauge, GROUP_INDENT, GroupChevron, Panel, plural, Pressable, RelativeTime, Sparkline } from '@orgus/ui';
import { BurnDown } from './FleetTable.tsx';
import { HealthBadge, ScoreText } from './HealthBadge.tsx';
import { HEALTH_TONE, ruleGroups, rulebookLabel } from './model.ts';
import type { RepoRow, RuleGroup } from './model.ts';
import { ViolationActions } from './ViolationActions.tsx';

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs text-fg-subtle">{label}</dt>
      <dd className="truncate text-sm text-fg num">{children}</dd>
    </div>
  );
}

function Group({ g, open, onToggle, row }: { g: RuleGroup; open: boolean; onToggle: () => void; row: RepoRow }) {
  const isError = g.errors > 0;
  const Icon = isError ? CircleX : TriangleAlert;
  const total = g.errors + g.warns;
  const hidden = total - g.violations.length;
  return (
    <li className="border-b border-border-subtle last:border-b-0">
      <Pressable onClick={onToggle} aria-expanded={open} className="flex items-center gap-3 px-4 py-2.5 hover:bg-hover">
        <span className="flex shrink-0 items-center gap-2">
          <GroupChevron open={open} />
          <Icon className={cn('size-4 shrink-0', isError ? 'text-status-danger' : 'text-status-attention')} aria-hidden />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-ui font-medium text-fg">{g.info.title}</span>
          <span className="truncate font-mono text-caption text-fg-subtle">{g.rule}</span>
        </span>
        {g.waived > 0 && <span className="hidden text-xs text-fg-subtle sm:inline">{formatInt(g.waived)} waived</span>}
        <span className={cn('w-24 shrink-0 text-right text-ui font-medium num', isError ? 'text-status-danger' : 'text-status-attention')}>{isError ? plural(g.errors, 'error') : plural(g.warns, 'warning')}</span>
      </Pressable>
      {open && (
        // One group step in from the header padding, under the rule's icon (GROUP_INDENT).
        <div className="px-4 pb-3">
          <div className={cn('flex flex-col gap-2', GROUP_INDENT)}>
            <p className="text-sm text-fg-muted">
              {g.info.why} <span className="text-fg">Fix: {g.info.fix}</span>
            </p>
            {g.violations.length > 0 && <ViolationActions target={{ projectId: row.projectId, projectName: row.name, violations: g.violations }} />}
            <ul className="flex flex-col overflow-hidden rounded-md border border-border-subtle bg-bg">
              {g.violations.map((v, i) => (
                <li key={`${v.path}:${v.line ?? 0}:${i}`} className="flex min-w-0 flex-col gap-0.5 border-b border-border-subtle px-3 py-1.5 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-3">
                  <span className="min-w-0 shrink-0 truncate font-mono text-xs text-fg sm:max-w-1/2">
                    {v.path}
                    {v.line ? <span className="text-fg-subtle">:{v.line}</span> : null}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-fg-muted">{v.message}</span>
                  {v.debt && (
                    <Badge size="sm" variant="outline" icon={<History />}>
                      Debt
                    </Badge>
                  )}
                </li>
              ))}
              {hidden > 0 && <li className="px-3 py-1.5 text-sm text-fg-subtle">{plural(hidden, 'more violation')} not shown. Run orgus check in the repo for the full list.</li>}
            </ul>
          </div>
        </div>
      )}
    </li>
  );
}

/** One repo: score and trend, what was checked, and its violations grouped by rule. */
export function RepoDetail({ row, onCheck, checking, onOnboard, kept }: { row: RepoRow; onCheck: () => void; checking: boolean; onOnboard: () => void; kept: boolean }) {
  const r = row.report;
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const groups = r && !r.error ? ruleGroups(r) : { failing: [], passing: [] };
  const [showPassing, setShowPassing] = useState(false);
  const isOpen = (rule: string, i: number) => open[rule] ?? i < 2;

  const checkButton = (
    <Button size="sm" variant="secondary" icon={<RefreshCw className={cn(checking && 'animate-spin')} />} onClick={onCheck} disabled={checking}>
      {checking ? 'Checking' : 'Check now'}
    </Button>
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Panel title={row.name} sub={r ? (kept ? 'Rulebook kept in Orgus' : rulebookLabel(r)) : undefined} icon={<ShieldCheck />} actions={checkButton}>
        {!r ? (
          <EmptyState
            icon={<Hourglass />}
            title="Waiting for the first check"
            description="The agent checks this repo every 10 minutes."
          />
        ) : r.error ? (
          <ErrorState
            variant="inline"
            error={{ what: `Could not check ${row.name}.`, why: r.error, next: 'Make sure the checkout exists on that machine and its agent is online, then check again.' }}
            onRetry={onCheck}
          />
        ) : (
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="flex items-center gap-4">
              <Gauge variant="ring" value={r.score / 100} tone={HEALTH_TONE[row.health]} label={`${row.name} discipline score`} valueText={<ScoreText health={row.health} score={r.score} className="text-lg" />} size={64} />
              <div className="flex flex-col gap-1">
                <HealthBadge health={row.health} />
                <span className="text-sm text-fg-subtle">
                  {plural(row.errors, 'error')}, {plural(row.warns, 'warning')}
                  {r.waived ? `, ${formatInt(r.waived)} waived` : ''}
                </span>
              </div>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1 md:items-end">
              <Sparkline values={row.history.map((p) => p.score)} min={0} max={100} width={220} height={40} tone="neutral" label={`${row.name} score over time`} />
              <span className="text-xs text-fg-subtle">{row.history.length > 1 ? `Score over the last ${plural(row.history.length, 'check')} that changed it` : 'The trend starts after the next change'}</span>
            </div>
          </div>
        )}
        {r && (
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border-subtle pt-3 sm:grid-cols-4">
            <Fact label="Checked">
              <RelativeTime ts={r.checkedAt} />
            </Fact>
            <Fact label="Machine">{row.machineName ?? 'Unknown machine'}</Fact>
            <Fact label="Files">{r.filesChecked ? `${formatInt(r.filesChecked)} in ${formatDuration(r.durationMs ?? 0)}` : '-'}</Fact>
            <Fact label="Checkout">
              <span className="font-mono text-xs" title={r.path}>
                {r.path ?? '-'}
              </span>
            </Fact>
          </dl>
        )}
      </Panel>

      {r && !r.error && (
        <Panel title="Ratchet" icon={<TrendingDown />} sub={r.ratchet ? 'New code must comply; old debt burns down' : undefined}>
          {r.ratchet ? (
            <div className="flex flex-wrap items-center gap-6">
              <BurnDown remaining={r.ratchet.remaining} total={r.ratchet.total} fresh={0} />
              <span className="text-sm text-fg-muted">
                {plural(r.ratchet.burned, 'item')} fixed since <RelativeTime ts={r.ratchet.since} />; {r.ratchet.fresh ? <span className="text-failed">{plural(r.ratchet.fresh, 'new violation')} to fix</span> : 'nothing new breaks a rule'}.
              </span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm text-fg-muted">Onboard to baseline today's debt and hold new code to a rulebook.</span>
              <Button size="sm" variant="secondary" icon={<ScanSearch />} onClick={onOnboard}>
                Onboard repo
              </Button>
            </div>
          )}
        </Panel>
      )}

      {r && !r.error && (
        <Panel title="Violations by rule" sub={groups.failing.length ? plural(groups.failing.length, 'rule') : undefined} flush>
          {r.truncated && (
            <p className="flex items-center gap-2 border-b border-border-subtle px-4 py-2 text-sm text-fg-muted">
              <Info className="size-3.5 shrink-0" aria-hidden />
              Long lists are cut to the first 50 per rule; the counts are exact.
            </p>
          )}
          {groups.failing.length ? (
            <ul>
              {groups.failing.map((g, i) => (
                <Group key={g.rule} g={g} row={row} open={isOpen(g.rule, i)} onToggle={() => setOpen((o) => ({ ...o, [g.rule]: !isOpen(g.rule, i) }))} />
              ))}
            </ul>
          ) : (
            <EmptyState icon={<CircleCheck />} title="Every rule passes" description={`${row.name} follows all ${plural(groups.passing.length, 'rule')} in its rulebook.`} />
          )}
          {groups.passing.length > 0 && (
            <div className="border-t border-border-subtle">
              <Pressable onClick={() => setShowPassing((v) => !v)} aria-expanded={showPassing} className="flex items-center gap-3 px-4 py-2.5 text-sm text-fg-muted hover:bg-hover">
                <span className="flex shrink-0 items-center gap-2">
                  <GroupChevron open={showPassing} />
                  <CircleCheck className="size-4 text-fg-subtle" aria-hidden />
                </span>
                {plural(groups.passing.length, 'rule')} {groups.passing.length === 1 ? 'passes' : 'pass'}
              </Pressable>
              {showPassing && (
                <ul className={cn('mx-4 grid grid-cols-1 gap-x-6 gap-y-1 pb-3 sm:grid-cols-2', GROUP_INDENT)}>
                  {groups.passing.map((g) => (
                    <li key={g.rule} className="flex min-w-0 items-center gap-2 text-sm text-fg-muted">
                      <CircleCheck className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                      <span className="truncate">{g.info.title}</span>
                      {g.waived > 0 && <span className="shrink-0 text-xs text-fg-subtle">({formatInt(g.waived)} waived)</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
